import React, { useState, useEffect, useRef } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Stack, Box, Container, Card, Grid, Button } from "@mui/material";
import Api from "../../network/Api";
import ScrollControl from "../Shared/ScrollControl";
import TypingIndicator from "../Shared/TypingIndicator";
import Markdown from 'react-markdown';
import wss from '../../channels/consumer';
import { Subscription } from "@rails/actioncable"
import { ConversationMessage } from "../../types/Conversation";
import SendButton from "./SendButton";
import { Lesson } from "../../types/Lessons";
import Questionaire, { Scores } from "../Lessons/Questionaire";
import Summary from "../Lessons/Summary";
import TaskAltIcon from '@mui/icons-material/TaskAlt';
 
const Chat = (props: { prompt?: string }) => {

  const [history, setHistory] = useState<Array<ConversationMessage>>([]);
  const [lesson, setLesson] = useState<Lesson>();
  const [prompt, setPrompt] = useState<string>(props.prompt || "");
  const [isAsking, shouldAsk] = useState<boolean>(false);
  const [inputDisabled, disableInput] = useState<boolean>(false);
  const [showsToolbox, showToolbox] = useState<boolean>(false);
  const [shouldContinue, askToContinue] = useState<boolean>(false);

  const { conversationId } = useParams();
  const subscription = useRef<Subscription>(null);

  const scrollBottomRef = useRef();

  const randomId = (): string => {
    return `${Math.floor(Math.random() * 100)}`;
  }

  useEffect(() => {
    subscription.current = wss.subscriptions.create({channel: "ConversationChannel", conversation_id: conversationId}, {
      connected() {
        if (prompt) {
          submitMessage();
        } else {
          Api.get(`/conversations/${conversationId}`).then(result => {
            setHistory(result.items.reverse());
            setLesson(result.lesson);
          })
        }
      },
      received(data) {
        switch(data.type) {
          case "chunk":
            upsertMessage(data.message, true);
            break;
          case "function":
            if (data.object.name === "continue") {
              askToContinue(true);
              break;
            }
            setHistory(current => [...current, { id: randomId(), role: "function", content: JSON.stringify(data.object) }]);
            break;
          case "lesson":
            setLesson(data.lesson);
            break;
          default: break;
        }
      }
    });

    return () => {
      subscription.current?.unsubscribe();
      subscription.current = undefined;
    }
  }, []);

  useEffect(() => {
    disableInput(isAsking);
  }, [isAsking]);

  const submitMessage = () => {
    if (prompt === "") { return }
    shouldAsk(true);
    setHistory(current => { return [...current, { id: randomId(), role: "user", content: prompt }] });
    setPrompt("");
    Api.put(`/conversations/${conversationId}`, { message: prompt }).then(onResponse);
  }

  const upsertMessage = (message: ConversationMessage, chunk: boolean) => {
    setHistory((current) => {
      let changedHistory = false;
      let newItems = current.map((item) => {
        if (item.id !== message.id) {
          return item;
        }
        changedHistory = true;
        return {...item, content: (chunk ? item.content + message.content : message.content)};
      });
      if (!changedHistory) {
        newItems = [...newItems, message];
      }
      return newItems;
    });
  }

  const onResponse = (result) => {
    shouldAsk(isAsking => {
      if (isAsking) {
        upsertMessage(result, false);
      }
      return false;
    })
  }

  useEffect(() => {
    if (scrollBottomRef.current) {
      scrollBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [history]);

  useEffect(() => {
    showToolbox(typeof(lesson) !== "undefined");
  }, [lesson]);

  const continueConversation = () => {
    if (isAsking) { return }
    askToContinue(false);
    shouldAsk(true);
    Api.put(`/conversations/${conversationId}`, { message: 'continue', temporary: true }).then(onResponse);
  }

  const updateScores = (scores: Scores) => {
    if (!lesson) { return }
    shouldAsk(true);
    Api.put(`/lessons/${lesson.id}`, scores).then(result => {
      setLesson(result);
      if (!lesson.frequency_score || !lesson.impact_score) {
        continueConversation();
      } else {
        shouldAsk(false);
      }
    });
  }

  const handleFunction = (object) => {
    switch(object.name) {
      case "start_questionaire":
        if (!lesson) { return }
        if (!inputDisabled) { disableInput(true) }
        return <Box className='animate__animated animate__fadeIn'>
          <Questionaire lesson={lesson} 
                        questions={object.params.questions}
                        onCompletion={updateScores} />
        </Box>
      default: 
        return <></>
    }
  }

  const renderSection = (item: ConversationMessage) => {
    switch(item.role) {
      case "assistant":
        return <Markdown>{item.content}</Markdown>
      case "user":
        return <Stack direction="row" justifyContent="flex-end"><Card elevation={0} sx={{ maxWidth: '75%', padding: "0.75rem 1rem !important", backgroundColor: "var(--color-greyDark) !important" }}><p>{item.content}</p></Card></Stack>
      case "function":
        return handleFunction(JSON.parse(item.content))
    }
  }

  return (
    <Stack
      className="animate__animated animate__fadeIn"
      marginY={"auto"}
      flexGrow={1}
      width="100%"
    >
      <Stack
        sx={{
          flex: "1 1 0",
          overflowY: "auto",
          pt: 4,
          alignItems: "center",
        }}
      >
        <Container maxWidth={showsToolbox ? "xl" : "md"}>
          <Grid container>
            <Grid size={{xs: 12, md: showsToolbox ? 6 : 12}}>
              <Stack spacing={3} sx={{position: "relative"}}>
                <ScrollControl backgroundColor="greyLight"><>
                
                  { history.map(item => <Box key={item.id}>{renderSection(item)}</Box>) }

                  { isAsking && <TypingIndicator /> }

                  { (shouldContinue || lesson?.summary) && !isAsking && <>
                    { lesson?.summary ? <Stack direction="row" gap={1} justifyContent="center">
                      <Button component={NavLink} variant="outlined" size="small" startIcon={<TaskAltIcon />} to={`/lessons/${lesson.id}`}>Store lesson learned</Button>
                      <Button component={NavLink} variant="outlined" color="secondary" size="small" to={`/lessons/${lesson.id}`}>Edit</Button>
                    </Stack> : 
                      <Button variant="outlined" size="small" onClick={continueConversation}>Continue</Button>
                    }
                  </> }

                  <Box ref={scrollBottomRef}></Box>

                </></ScrollControl>
              </Stack>
            </Grid>

            { showsToolbox && lesson && <Grid size={{xs: 12, md: 6}}>
              <Card sx={{position: "sticky", left: 0, top: 0, marginLeft: { xs: 0, md: 6 }, right: 0, height: { md: "calc(100vh - 228px - 6rem)" }}} className="animate__animated animate__fadeInUp">
                <Box sx={{height: "100%", overflow: "hidden", mr: -3}}>
                  <Box sx={{overflowY: 'auto', height: "100%", pr: 2}}>
                    <ScrollControl backgroundColor="white">
                      <Summary lesson={lesson} animated preview />
                    </ScrollControl>
                  </Box>
                </Box>
              </Card>
            </Grid> }

          </Grid>
        </Container>
      </Stack>

      <Container maxWidth={showsToolbox ? "xl" : "md"} sx={{ mb: 3 }}>
        <Grid container>
          <Grid size={{xs: 12, md: showsToolbox ? 6 : 12}}>
            <SendButton value={prompt}
                        disabled={inputDisabled}
                        onChange={setPrompt}
                        onClick={submitMessage} />
            <footer className="text-secondary text-center"><small>AI can make mistakes. Consider fact checking important information.</small></footer>
          </Grid>
        </Grid>
      </Container>
    </Stack>
  );
};

export default Chat;
